// =============================================================================
// VandieZ admin-web -- Trips at risk (/at-risk)
// =============================================================================
// Lists active trips that match one or more risk heuristics computed by
// GET /admin/trips-at-risk. Each row shows the route, parties, and a
// severity score that sorts the list. Click a row to open the trip
// detail page for triage.
//
// 5 heuristics (mirror admin_at_risk.py):
//   stuck_in_transit  - in_transit > 24h since pickup           (sev 3)
//   open_dispute       - >=1 open / under_review dispute         (sev 2)
//   multiple_releases  - replacement_count > 1, still active     (sev 2)
//   pod_overdue        - delivered > 2h, PoD still pending       (sev 2)
//   payment_stuck      - completed > 12h, commission uncollected (sev 3)
// =============================================================================

import {
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  PackageSearch,
  RefreshCw,
  Siren,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { atRiskAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AtRiskLoadRow, AtRiskTripRow } from "@/types/at_risk";

const POLL_MS = 60_000;

function severityClass(score: number): string {
  if (score >= 5) {return "bg-danger-subtle text-danger-subtle-foreground";}
  if (score >= 3) {return "bg-warning-subtle text-warning-subtle-foreground";}
  return "bg-muted text-muted-foreground";
}

const REASON_LABEL: Record<string, string> = {
  stuck_in_transit: "Stuck in transit",
  open_dispute: "Open dispute",
  multiple_releases: "Multiple releases",
  pod_overdue: "PoD overdue",
  payment_stuck: "Payment stuck",
};

// Loads at risk (AD-06) — mirror the load heuristics in admin_at_risk.py.
const LOAD_REASON_LABEL: Record<string, string> = {
  no_bids_window_closing: "No bids, window closing",
  expired_unfilled: "Expired unfilled",
  no_bids_stale: "No bids, stale",
};

export function AtRiskPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<AtRiskTripRow[]>([]);
  const [totalActive, setTotalActive] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Separate state, and a separate fetch, on purpose: a failure in one
  // feed must not blank the other. Ops triaging stuck trips should not
  // lose that list because the loads query timed out.
  const [loadRows, setLoadRows] = useState<AtRiskLoadRow[]>([]);
  const [loadsLoading, setLoadsLoading] = useState(true);
  const [loadsError, setLoadsError] = useState<string | null>(null);

  const fetchRows = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      try {
        const res = await atRiskAdminApi.list(
          accessToken,
          { limit: 100 },
          signal,
        );
        setRows(res.rows);
        setTotalActive(res.total_active_trips);
        setError(null);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load at-risk trips.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const fetchLoadRows = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      try {
        const res = await atRiskAdminApi.listLoads(
          accessToken,
          { limit: 100 },
          signal,
        );
        setLoadRows(res.items);
        setLoadsError(null);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setLoadsError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load at-risk loads.",
        );
      } finally {
        setLoadsLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchRows(c.signal);
    void fetchLoadRows(c.signal);
    const id = window.setInterval(() => {
      void fetchRows();
      void fetchLoadRows();
    }, POLL_MS);
    return () => {
      c.abort();
      window.clearInterval(id);
    };
  }, [fetchRows, fetchLoadRows]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="At risk"
        description="Active trips going wrong, and open loads about to expire unfilled. Auto-refreshes every minute. Click a row to open its detail page for triage."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void fetchRows();
              void fetchLoadRows();
            }}
            disabled={loading || loadsLoading}
          >
            <RefreshCw
              className={cn(
                "mr-1 h-4 w-4",
                (loading || loadsLoading) && "animate-spin",
              )}
            />
            Refresh
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Siren className="h-5 w-5" /> Flagged trips
          </CardTitle>
          <CardDescription>
            5 heuristics — stuck in transit ({">"}24h), open dispute,
            multiple re-pairings, PoD overdue ({">"}2h after delivered),
            payment stuck ({">"}12h after completed). Sorted by total
            severity descending.
            <span className="ml-1 text-foreground">
              {rows.length} flagged
              {totalActive !== null && (
                <> out of {totalActive} active</>
              )}.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && rows.length === 0 ? (
            <TableSkeleton rows={5} columns={4} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No trips at risk right now. All active trips look healthy.
            </div>
          ) : (
            <ul className="divide-y">
              {rows.map((r) => (
                <li key={r.trip_id}>
                  <Link
                    to={`/trips/${r.trip_id}`}
                    className="flex items-start gap-3 px-2 py-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="mt-0.5 shrink-0">
                      <AlertTriangle className="h-4 w-4 text-warning-subtle-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-muted-foreground">
                            {r.trip_id.slice(0, 8)}
                          </span>
                          <span className="text-xs uppercase tracking-wide text-muted-foreground">
                            {r.status.replace(/_/g, " ")}
                          </span>
                          <span className="truncate text-sm">
                            {r.pickup_district ?? "?"} →{" "}
                            {r.drop_district ?? "?"}
                          </span>
                        </div>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium shrink-0",
                            severityClass(r.total_score),
                          )}
                        >
                          score {r.total_score}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {r.shipper_name ?? "shipper ?"} ·{" "}
                        {r.driver_name ?? "driver ?"}
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {r.reasons.map((s) => (
                          <li
                            key={s.code}
                            className="text-xs text-muted-foreground flex items-center gap-2"
                          >
                            <span className="font-mono rounded bg-muted px-1.5 py-0.5">
                              {REASON_LABEL[s.code] ?? s.code} (sev {s.severity})
                            </span>
                            <span>{s.detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageSearch className="h-5 w-5" /> Loads at risk
          </CardTitle>
          <CardDescription>
            Open loads failing to attract a driver — no bids with under
            2h left, window closed unfilled, or open {">"}24h with no
            bids. These expire quietly unless somebody calls a driver.
            <span className="ml-1 text-foreground">
              {loadRows.length} flagged.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadsError !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{loadsError}</AlertDescription>
            </Alert>
          )}

          {loadsLoading && loadRows.length === 0 ? (
            <TableSkeleton rows={4} columns={4} />
          ) : loadRows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No loads at risk right now. Every open load is attracting
              bids.
            </div>
          ) : (
            <ul className="divide-y">
              {loadRows.map((r) => (
                <li key={r.load_id}>
                  <Link
                    to={`/loads/${r.load_id}`}
                    className="flex items-start gap-3 px-2 py-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="mt-0.5 shrink-0">
                      <AlertTriangle className="h-4 w-4 text-warning-subtle-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-xs text-muted-foreground">
                            {r.load_id.slice(0, 8)}
                          </span>
                          <span className="text-xs uppercase tracking-wide text-muted-foreground">
                            {r.status.replace(/_/g, " ")}
                          </span>
                          <span className="truncate text-sm">
                            {r.pickup_district} → {r.drop_district}
                          </span>
                        </div>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-xs font-medium shrink-0",
                            severityClass(r.score),
                          )}
                        >
                          score {r.score}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {r.shipper_name ?? "shipper ?"} ·{" "}
                        {r.bid_count === 0
                          ? "no bids"
                          : `${r.bid_count} bid${r.bid_count === 1 ? "" : "s"}`}
                      </div>
                      <ul className="mt-1 space-y-0.5">
                        {r.reasons.map((s) => (
                          <li
                            key={s.code}
                            className="text-xs text-muted-foreground flex items-center gap-2"
                          >
                            <span className="font-mono rounded bg-muted px-1.5 py-0.5">
                              {LOAD_REASON_LABEL[s.code] ?? s.code} (sev{" "}
                              {s.severity})
                            </span>
                            <span>{s.detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
